import { isNullOrUndefined } from 'src/app/shared/utils';

export interface INamedElement {
  readonly name: string;
}

export abstract class ElementCollectionBase<K, T> implements Iterable<T> {
  protected readonly _dict = new Map<any, T>();

  get dict(): ReadonlyMap<K, T> {
    return this._dict;
  }

  get length() {
    return this._dict.size;
  }


  at(index: number): T {
    if (index < 0 || index >= this._dict.size)
      throw new Error(`Invalid index`, { cause: this });

    for (let i of this._dict.values()) {
      if (index == 0)
        return i;

      index--;
    }
  }

  find(key: K): T | null {
    let k = this.normalizeKey(key);

    for (let collection of this.all()) {
      let item = collection.dict.get(k);
      if (item)
        return item;
    }

    return null;
  }

  get(key: K): T {
    let item = this.find(key);
    if (!item)
      throw new Error(`Could not match item to key '${key}'`);

    return item;
  }

  protected normalizeKey(k: K): any {
    return (typeof k === 'string') ? k.toLowerCase()
         : (typeof k === 'object') ? JSON.stringify(k)
         : k;
  }


  *keys(): Iterable<K> {
    for (let collection of this.all()) {
      for (let key of collection.dict.keys())
        yield key;
    }
  }

  *values(): Iterable<T> {
    for (let collection of this.all()) {
      for (let item of collection.dict.values())
        yield item;
    }
  }

  forEach(callback: (item: T) => void) {
    for (let collection of this.all()) 
      collection.dict.forEach((v, k) => callback(v));
  }

  *map<R>(transform: (item: T, index?: number) => R): Iterable<R> {
    let index = 0;

    for (let item of this)
      yield transform(item, index++);
  }

  protected *all(): Iterable<ElementCollectionBase<K, T>> {
    for (let extension of this.getExtensions()) {
      if (extension) {
        yield extension;
      }
    }

    yield this;

    let base = this.getBase();
    if (base) {
      yield base;
    }
  }

  filter(predicate: (item: T) => boolean): T[] {
    let result: T[] = [];

    for (let item of this.values()) {
      if (predicate(item))
        result.push(item);
    }

    return result;
  }
  
  firstMatch(predicate: (item: T) => boolean): T | undefined {
    for (let item of this.values()) {
      if (predicate(item))
        return item;
    }

    return undefined;
  }

  first() {
    for (let item of this.values()) {
      return item;
    }

    return undefined;
  }


  protected getBase(): ElementCollectionBase<K, T> { return null; }
  protected *getExtensions(): Iterable<ElementCollectionBase<K, T>> { }

  *[Symbol.iterator](): Iterator<T> {
    for (let item of [...this.values()])
      yield item;
  }
}

export abstract class ElementCollection<K, T> extends ElementCollectionBase<K, T> {
  protected _add(item: T) {
    let key = this.normalizeKey(this.getKey(item));
    if (this._dict.get(key))
      throw new Error(`Item with key already added`, { cause: item });

    this._dict.set(key, item);
  }

  protected _remove(item: T) {
    return this._dict.delete(this.normalizeKey(this.getKey(item)));
  }

  protected abstract getKey(item: T): K;
}

export abstract class NamedElementCollection<T extends INamedElement> extends ElementCollection<string, T> {
  protected getKey(item: T) { return item.name; }
}

export abstract class NamedElementHierarchyCollection<T extends INamedElement> extends NamedElementCollection<T> {
  find(...path: string[]): T | null {
    if (!path?.length)
      return null;

    let item = super.find(path[0]);
    if (!item)
      return null;

    for (let i = 1; i < path.length; ++i) {
      let part = path[i];
      if (isNullOrUndefined(part))
        break;

      item = this.findChild(item, path[i]);
      if (!item)
        return null;
    }

    return item;
  }

  get(...path: string[]): T | null {
    let item = this.find(...path);
    if (!item)
      throw new Error(`Could not match item to key.`, { cause: `[${path.join('].[')}]` });

    return item;
  }


  protected findChild(item: T, name: string) { return null; }
}
